import React, { useEffect, useState } from "react";
import Modal, { ModalFooter } from "konnekt/Modal";
import {
  getCountryNameFromCountryInitials,
  showNotification,
} from "util/utility";
import { stateList } from "constants/stateConstants";
import useOrganizationDetails from "customHooks/Organization/useOrganizationDetails";
import { useDispatch, useSelector } from "react-redux";
import {
  getAuthenticatedState,
  getOrganizationUpdateStatus,
} from "modules/App/AppSelectors";
import { toggleEditAddressModalState } from "modules/Payments/PaymentActions";
import {
  setOrganizationDetailInputValues,
  updateOrganization,
} from "modules/App/AppActions";
import useOrganizationId from "customHooks/useOrganizationId";
import Box from "konnekt/Box";
import Dropdown from "konnekt/Dropdown";
import { getIsHaptikUser } from "modules/App/HaptikReducer";
import { getEditAddressModalState } from "modules/Payments/PaymentSelectors";
import useIsOrgAddressAdded from "customHooks/Organization/useIsOrgAddressAdded";
import useIsSandboxMode from "customHooks/useIsSandboxMode";
import KiwiImage from "components/KiwiImage";
import CountryDropdown from "../CountryDropdown";
import useIsResellerSignupSource from "customHooks/useIsResellerSignupSource";

const AddBusiness = () => {
  const dispatch = useDispatch();

  const showModal = useSelector(getEditAddressModalState);

  const addressAdded = useIsOrgAddressAdded();

  const isAuthenticated = useSelector(getAuthenticatedState);

  const isSandBoxMode = useIsSandboxMode();

  const [countryInput, setCountryInput] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [cityInput, setCityInput] = useState("");
  const [stateInput, setStateInput] = useState("");
  const [pincodeInput, setPincodeInput] = useState("");

  const organization = useOrganizationDetails();
  const organizationId = useOrganizationId();

  const isUpdatingOrganization = useSelector(getOrganizationUpdateStatus);
  const isResellerSignupSource = useIsResellerSignupSource();

  const isHaptikUser = getIsHaptikUser();

  useEffect(() => {
    if (
      isAuthenticated &&
      !addressAdded &&
      organizationId &&
      !isSandBoxMode &&
      !isHaptikUser
    ) {
      dispatch(toggleEditAddressModalState(true));
    }
  }, [
    isAuthenticated,
    addressAdded,
    organizationId,
    isSandBoxMode,
    isHaptikUser,
  ]);

  useEffect(() => {
    setCountryInput(organization?.country_initials ?? "");
    setAddressLine1(organization?.address_line_1 ?? "");
    setAddressLine2(organization?.address_line_2 ?? "");
    setCityInput(organization?.city ?? "");
    setStateInput(organization?.state ?? "");
    setPincodeInput(organization?.pincode ?? "");
  }, [
    organization?.country_initials,
    organization?.address_line_1,
    organization?.address_line_2,
    organization?.city,
    organization?.state,
    organization?.pincode,
    showModal,
  ]);

  const handleUpdateOrganization = (data) => {
    if (organizationId) {
      dispatch(updateOrganization(organizationId, data, false));
    }
  };

  const handleToggleBusiness = (state) => {
    dispatch(toggleEditAddressModalState(!!state));
    dispatch(setOrganizationDetailInputValues(organization));
  };

  const handleSubmit = () => {
    if (
      organization.name &&
      countryInput &&
      addressLine1 &&
      addressLine2 &&
      cityInput &&
      stateInput &&
      pincodeInput
    ) {
      handleUpdateOrganization({
        ...organization,
        country: countryInput,
        country_initials: countryInput,
        address_line_1: addressLine1,
        address_line_2: addressLine2,
        city: cityInput,
        state: stateInput,
        pincode: pincodeInput,
      });

      handleToggleBusiness(false);
    } else {
      showNotification("warning", "All fields are mandatory");
    }
  };

  const handleCountryChange = (option) => {
    setStateInput("");
    setCountryInput(option.value ?? "IN");
  };

  const getCurrentCountry = () => {
    const country = organization?.country;

    if (countryInput) {
      return getCountryNameFromCountryInitials(countryInput);
    }

    if (country) {
      return getCountryNameFromCountryInitials(country) ?? country;
    }

    return "Country";
  };

  if (!isAuthenticated) {
    return null;
  }

  if (!showModal) {
    return null;
  }

  const renderState = () => {
    if (countryInput === "IN") {
      return (
        <Dropdown
          allowSearch
          options={stateList.IN}
          selectedValue={stateInput}
          onOptionClick={(option) => setStateInput(option.value)}
          width="100%"
          height="40px"
          borderRadius="6px"
          fontWeight={400}
          fontSize="1rem"
        />
      );
    }

    return (
      <input
        onChange={(e) => setStateInput(e.target.value)}
        type="text"
        className="form-control custom-full-input-field"
        placeholder="State"
        value={stateInput}
      />
    );
  };

  if (isResellerSignupSource) {
    return null;
  }

  return (
    <Modal
      isOpen
      onHide={() => {
        if (addressAdded) {
          handleToggleBusiness(false);
        }
      }}
      borderRadius="6px"
      boxShadow="4px 4px 4px rgba(166, 166, 166, 0.25)"
    >
      <div>
        <div className="file-header" style={{ height: "70px" }}>
          <div>
            <span>
              <KiwiImage targetClass="" iconName="edit-reply.svg" />
            </span>
            <span
              className="kiwi-color-white kiwi-h2"
              style={{ marginLeft: "10px" }}
            >
              Business Address
            </span>
          </div>
          <div>
            {addressAdded && (
              <Box
                className="cursor-pointer"
                style={{ marginLeft: "25px" }}
                onClick={() => handleToggleBusiness(false)}
              >
                <img
                  src="https://stkiwiwebdev.z23.web.core.windows.net/images/customize-close.svg"
                  alt=""
                />
              </Box>
            )}
          </div>
        </div>
        <div style={{ padding: "20px 20px 20px 20px" }}>
          <div className="row">
            <div className="col-12">
              <div style={{ width: "150px", marginBottom: "18px" }}>
                <CountryDropdown
                  selectedValue={getCurrentCountry()}
                  onOptionClick={handleCountryChange}
                />
              </div>
            </div>
            <div className="col-12">
              <div className="form-group">
                <input
                  type="text"
                  className="form-control custom-full-input-field"
                  placeholder="Street Address Line 1"
                  value={addressLine1}
                  onChange={(e) => setAddressLine1(e.target.value)}
                />
              </div>
            </div>
            <div className="col-12">
              <div className="form-group">
                <input
                  type="text"
                  className="form-control custom-full-input-field"
                  placeholder="Street Address Line 2"
                  value={addressLine2}
                  onChange={(e) => setAddressLine2(e.target.value)}
                />
              </div>
            </div>
            <div className="col-6">
              <div className="form-group">
                <input
                  type="text"
                  className="form-control custom-full-input-field"
                  placeholder="City"
                  value={cityInput}
                  onChange={(e) => setCityInput(e.target.value)}
                />
              </div>
            </div>
            <div className="col-6">{renderState()}</div>

            <div className="col-12">
              <div className="form-group">
                <input
                  type="text"
                  className="form-control custom-full-input-field"
                  placeholder="Pincode"
                  value={pincodeInput}
                  onChange={(e) => setPincodeInput(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
        <ModalFooter display="flex" justifyContent="flex-end" px="30px">
          <button
            onClick={() => handleSubmit()}
            disabled={isUpdatingOrganization}
            type="button"
            className="btn btn-green"
            style={{ height: "40px", fontWeight: "600" }}
          >
            {isUpdatingOrganization ? "Updating..." : "Update"}
          </button>
        </ModalFooter>
      </div>
    </Modal>
  );
};

export default AddBusiness;
